import { userApi } from "@/lib/api/endpoints/user";
import { isNative, isStandalonePwa } from "@/lib/native/capacitor";
import { vapidPublicKey } from "@/lib/utils/env";

/**
 * Browser push for the web app (#385). The native apps get push through
 * Capacitor (lib/native/push.ts); here the same "new activity" message arrives
 * over the Web Push protocol via public/sw.js. Everything is a no-op inside
 * the native shells.
 *
 * States, in the order the Notifications card checks them:
 *  - unsupported:   no service worker / PushManager, or no VAPID key configured
 *  - needs-install: iOS Safari in a tab - Apple only allows push from a
 *                   home-screen app (iOS 16.4+), so the user must install first
 *  - blocked:       the browser permission was denied; only its site settings
 *                   can undo that
 *  - off / on:      no subscription / subscribed and registered with the API
 */
export type WebPushState = "unsupported" | "needs-install" | "blocked" | "off" | "on";

const SW_URL = "/sw.js";

function isIosBrowser(): boolean {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  // iPadOS reports itself as a Mac; the touch points give it away.
  return /iPad|iPhone|iPod/.test(ua) || (navigator.platform === "MacIntel" && (navigator.maxTouchPoints ?? 0) > 1);
}

export function webPushSupport(): "ok" | "unsupported" | "needs-install" {
  if (isNative()) return "unsupported";
  if (typeof window === "undefined" || typeof navigator === "undefined") return "unsupported";
  if (!("serviceWorker" in navigator) || !("PushManager" in window) || !("Notification" in window)) {
    return "unsupported";
  }
  if (!vapidPublicKey()) return "unsupported";
  if (isIosBrowser() && !isStandalonePwa()) return "needs-install";
  return "ok";
}

/** The identity of a subscription everywhere: register, unregister and dedupe compare this string. */
export const subscriptionToken = (sub: PushSubscription): string => JSON.stringify(sub.toJSON());

/** VAPID keys are URL-safe base64; PushManager wants the raw bytes. */
export function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

async function currentSubscription(): Promise<PushSubscription | null> {
  const reg = await navigator.serviceWorker.getRegistration(SW_URL);
  if (!reg) return null;
  return reg.pushManager.getSubscription();
}

export async function getWebPushState(): Promise<WebPushState> {
  const support = webPushSupport();
  if (support !== "ok") return support;
  if (Notification.permission === "denied") return "blocked";
  try {
    return (await currentSubscription()) ? "on" : "off";
  } catch {
    return "off";
  }
}

/**
 * Turn notifications on. Must be called from a click handler: browsers only
 * honour a permission request made inside a user gesture.
 */
export async function enableWebPush(): Promise<WebPushState> {
  const support = webPushSupport();
  if (support !== "ok") return support;
  const permission = await Notification.requestPermission();
  if (permission !== "granted") return permission === "denied" ? "blocked" : "off";

  const reg = await navigator.serviceWorker.register(SW_URL, { scope: "/" });
  await navigator.serviceWorker.ready;
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapidPublicKey()) as BufferSource,
    });
  }
  await userApi.registerDevice(subscriptionToken(sub), "WEB");
  return "on";
}

/** Turn notifications off on this browser: forget it server-side, then drop the subscription. */
export async function disableWebPush(): Promise<WebPushState> {
  const sub = await currentSubscription();
  if (!sub) return "off";
  const token = subscriptionToken(sub);
  try {
    await userApi.unregisterDevice(token);
  } finally {
    await sub.unsubscribe();
  }
  return "off";
}

/**
 * On login: an existing subscription belongs to whoever is signed in now.
 * Registering is idempotent server-side, so this just re-links it.
 */
export async function syncWebPushRegistration(): Promise<void> {
  if (webPushSupport() !== "ok" || Notification.permission !== "granted") return;
  try {
    const sub = await currentSubscription();
    if (sub) await userApi.registerDevice(subscriptionToken(sub), "WEB");
  } catch {
    /* best effort */
  }
}

/**
 * On logout: unsubscribe so the next account on this browser does not
 * receive the previous one's pushes. `guid` is the key of the session being
 * ended - the store has already cleared it by the time this runs.
 */
export async function forgetWebPush(guid: string | null): Promise<void> {
  if (webPushSupport() !== "ok") return;
  try {
    const sub = await currentSubscription();
    if (!sub) return;
    const token = subscriptionToken(sub);
    if (guid) await userApi.unregisterDevice(token, { authKeyOverride: guid }).catch(() => {});
    await sub.unsubscribe();
  } catch {
    /* best effort */
  }
}
